import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';

import {MapService} from '@commons/services/map/map/map.service';
import {PopupDrawerService} from '@commons/services/map/drawer/popup/popup-drawer.service';
import {VehicleService} from '@commons/services/vehicle/vehicle.service';
import {MapConfigService} from '@commons/services/map/config/map-config.service';

import {PermissionService} from "@shared/services/permission.service";
import {BaseComponent} from "@shared/components/base-component";
import {AuthenticationService} from '@shared/services/authentication/authentication.service';
import {SettingsService} from "@shared/services/settings.service";

import {MapPanelService} from "@map-panel/services/map-panel.service";
import {MapLayerType} from '../map-layers';
import { NewNavigationService } from '@shared/services/new-navigation.service';
import { MapActionType } from '@src/app/enum/map.enum';

@Component({
  selector: 'tk-map-controls',
  templateUrl: './map-controls.component.html',
  styleUrls: ['./map-controls.component.less']
})
export class MapControlsComponent extends BaseComponent implements OnInit, OnDestroy {

  @Input() public hasAccessToAreas: boolean;
  @Input() public isAreasPanelActive: boolean;

  @Output() public onClickModule$: EventEmitter<MapActionType> = new EventEmitter();

  readonly clientName?: string = '';

  isFullScreenActive = false;
  isCenterMapActive = false;
  isShowPopupActive = false;
  isMapPanelVisible = false;
  followVehicleStatus = false;
  isTrackingVisible = false;
  mainMapZoomLevelOffset = 0;

  get isEmapaSelected(): boolean {
    return this.mapService.currentMapLayerType === MapLayerType.emapa;
  }

  get isOpenStreetSelected(): boolean {
    return this.mapService.currentMapLayerType === MapLayerType.osm;
    // ||this.mapService.currentMapLayerType === MapLayerType.osmTransport;
  }

  get isGoogleSelected(): boolean {
    return this.mapService.currentMapLayerType === MapLayerType.googleRoadmap ||
      this.mapService.currentMapLayerType === MapLayerType.googleHybrid;
  }

  get isWindowTheSameSizeAsScreen(): boolean {
    return (window.innerHeight == screen.height || window.innerHeight == screen.height - 1) && window.innerWidth == screen.width;
  }

  constructor(
    private mapConfigService: MapConfigService,
    private popupDrawerService: PopupDrawerService,
    private router: Router,
    private vehicleService: VehicleService,
    private permissionService: PermissionService,
    private authenticationService: AuthenticationService,
    private mapPanelService: MapPanelService,
    private mapService: MapService,
    private settingsService: SettingsService,
    private newNavigationService: NewNavigationService,
  ) {
    super();
    this.isFullScreenActive = this.isInFullScreen();
    this.clientName = this.authenticationService.currentClient;
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  ngOnInit() {
    this.isMapPanelVisible = true;
    this.onSwitchMap();
    this.subToIsMapPanelVisible();

    if (this.settingsService.isShowPopups())
      this.onShowPopups();

    this.subToCurrentVehicleIdChange();
    this.subToGetFollowVehicleSubject();
    this.subToMouseDownWhenMapReady();
  }

  public onOpenAreasPanel(): void {
    this.onClickModule$.emit(MapActionType.Areas);
  }

  onTracking() {
    this.followVehicleStatus = !this.followVehicleStatus;
    this.vehicleService.setFollowVehicleStatus(this.followVehicleStatus);
  }

  onZoomIn(): void {
    this.mapService.changeZoom(+1);
    this.onMainMapIncreaseZoom();
  }

  onZoomOut(): void {
    this.mapService.changeZoom(-1);
    this.onMainMapDecreaseZoom();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isFullScreenActive = this.isInFullScreen();
  }

  onToggleFullScreen() {
    if (this.isInFullScreen()) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }

    this.isFullScreenActive = this.isInFullScreen();
  }

  onCenterMap(): void {
    this.mapService.centerMap();
  }

  onShowPopups(): void {
    this.isShowPopupActive = this.popupDrawerService.toggleClusterPopupsVisibility();
    this.settingsService.setShowPopups(this.isShowPopupActive);
  }

  onSwitchMap(emit = true) {
    this.isMapPanelVisible = !this.isMapPanelVisible;
    if (emit)
      this.mapPanelService.isMapPanelVisibleSubject.next(this.isMapPanelVisible);
  }

  private subToGetFollowVehicleSubject() {
    this.vehicleService.getFollowVehicleSubject().subscribe(isFollowed => {
      this.followVehicleStatus = isFollowed;
    });
  }

  private subToCurrentVehicleIdChange() {
    this.mapConfigService.currentVehicleIdChange().subscribe(data => {
      this.isTrackingVisible = !isNaN(data.currentVehicleId);
    });
  }

  private subToMouseDownWhenMapReady() {
    this.mapService.mapReady$.subscribe(isReady => {
      if (isReady) {
        // event moved dla mapy jest zgłaszany też kiedy pojazd jest śledzony więc odpięcie śledzenia odbywa się po kliknięciu na mapkę
        // co najprawdopodobniej będzie początkiem ruchu przesunięcia mapki
        this.mapService.map?.on('mousedown', () => {
          this.followVehicleStatus = false;
          this.vehicleService.setFollowVehicleStatus(this.followVehicleStatus);
        });
      }
    });
  }

  private enterFullscreen(): void {
    const _Element = <any>Element;
    const elem = <any>document.body;

    if (elem.requestFullScreen) {
      elem.requestFullScreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullScreen) {
      elem.webkitRequestFullScreen(_Element.ALLOW_KEYBOARD_INPUT);
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }

  private exitFullscreen(): void {
    const _document = <any>document;

    if (_document.cancelFullScreen) {
      _document.cancelFullScreen();
    } else if (_document.mozCancelFullScreen) {
      _document.mozCancelFullScreen();
    } else if (_document.webkitCancelFullScreen) {
      _document.webkitCancelFullScreen();
    } else if (_document.msExitFullscreen) {
      _document.msExitFullscreen();
    }
  }

  private isInFullScreen(): boolean {
    const _document = <any>document;
    var domFullscreen: boolean;
    if (
      (_document.fullScreenElement !== undefined && _document.fullScreenElement === null) ||
      (_document.msFullscreenElement !== undefined && _document.msFullscreenElement === null) ||
      (_document.mozFullScreen !== undefined && !_document.mozFullScreen) ||
      (_document.webkitIsFullScreen !== undefined && !_document.webkitIsFullScreen)) {
      domFullscreen = false;
    } else {
      domFullscreen = true;
    }

    return this.isWindowTheSameSizeAsScreen || domFullscreen;
  }

  private subToIsMapPanelVisible() {
    this.addToUnsubscribe(
      this.mapPanelService.isMapPanelVisibleSubject.subscribe(isVisible => {
        this.isMapPanelVisible = isVisible;
      })
    );
  }

  private onMainMapIncreaseZoom() {
    if (this.mainMapZoomLevelOffset === 0)
      return;

    this.mainMapZoomLevelOffset += 1;
    this.mapPanelService.mainMapZoomLevelOffsetChanged.next(this.mainMapZoomLevelOffset);
  }

  private onMainMapDecreaseZoom() {
    if (this.mainMapZoomLevelOffset === -10)
      return;

    this.mainMapZoomLevelOffset -= 1;
    this.mapPanelService.mainMapZoomLevelOffsetChanged.next(this.mainMapZoomLevelOffset);
  }

}
